import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, Globe } from 'lucide-react';
import { SettingRow } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Button,
  IconButton,
  Input,
  Switch,
  Badge,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import { useCdnConfig, useUpdateCdnConfig, useDeleteCdnConfig } from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';
import { ConfigFormShell, jsonEqual } from '../../_shared/ConfigFormShell';
import { CdnAspectDrift } from '../../_shared/VendorDrift';

interface CorsRule {
  _id: string;
  priority: number;
  path_pattern: string;
  match_type: string;
  allow_origins: string[];
  allow_methods: string[];
  allow_headers: string[];
  expose_headers: string[];
  allow_credentials: boolean;
  max_age_secs: number;
}
const MATCH_TYPES = ['prefix', 'exact', 'wildcard', 'regex'];
let _cid = 0;
const lid = () => `co${++_cid}`;

const csv = (a: unknown) => (Array.isArray(a) ? (a as string[]).join(', ') : '');
const split = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

function hydrate(c: Record<string, unknown>): { enabled: boolean; rules: CorsRule[] } {
  const rules = Array.isArray(c.rules) ? (c.rules as Record<string, unknown>[]) : [];
  return {
    enabled: Boolean(c.enabled),
    rules: rules.map((r) => ({
      _id: lid(),
      priority: Number(r.priority) || 0,
      path_pattern: (r.path_pattern as string) || '/',
      match_type: (r.match_type as string) || 'prefix',
      allow_origins: Array.isArray(r.allow_origins) ? (r.allow_origins as string[]) : [],
      allow_methods: Array.isArray(r.allow_methods) ? (r.allow_methods as string[]) : [],
      allow_headers: Array.isArray(r.allow_headers) ? (r.allow_headers as string[]) : [],
      expose_headers: Array.isArray(r.expose_headers) ? (r.expose_headers as string[]) : [],
      allow_credentials: Boolean(r.allow_credentials),
      max_age_secs: Number(r.max_age_secs) || 0,
    })),
  };
}

export function CorsTab({ domain, domainId }: { domain: string; domainId: string }) {
  const { show } = useToast();
  const { data: cfg, isLoading } = useCdnConfig(domainId, 'cors', { vendorMarkers: ['enabled', 'rules'] });
  const isConfigured = !!cfg;

  const [enabled, setEnabled] = useState(false);
  const [rules, setRules] = useState<CorsRule[]>([]);
  const [snap, setSnap] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    const next = cfg ? hydrate(cfg) : { enabled: false, rules: [] };
    setEnabled(next.enabled);
    setRules(next.rules);
    setSnap(JSON.stringify(next));
  }, [cfg]);
  const dirty = useMemo(() => !jsonEqual({ enabled, rules }, JSON.parse(snap || '{}')), [enabled, rules, snap]);
  const patch = (id: string, c: Partial<CorsRule>) => setRules((p) => p.map((r) => (r._id === id ? { ...r, ...c } : r)));

  const update = useUpdateCdnConfig(domainId, 'cors', {
    onSuccess: () => {
      trackFeatureAction('cdn', 'cors', isConfigured ? 'edit' : 'create');
      show({ tone: 'success', title: 'CORS saved' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not save CORS', description: errorMessage(e, 'Could not save CORS') }),
  });
  const remove = useDeleteCdnConfig(domainId, 'cors', {
    onSuccess: () => {
      trackFeatureAction('cdn', 'cors', 'delete');
      show({ tone: 'success', title: 'CORS removed' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not remove CORS', description: errorMessage(e, 'Could not remove CORS') }),
  });
  const busy = update.isPending || remove.isPending;

  const addRule = () => {
    const r: CorsRule = { _id: lid(), priority: rules.length ? Math.max(...rules.map((x) => x.priority)) + 10 : 100, path_pattern: '/', match_type: 'prefix', allow_origins: ['*'], allow_methods: ['GET', 'POST'], allow_headers: [], expose_headers: [], allow_credentials: false, max_age_secs: 86400 };
    setRules((p) => [...p, r]);
    setExpanded((s) => new Set(s).add(r._id));
  };

  const save = () =>
    update.mutate({
      enabled,
      rules: rules.map(({ _id, ...r }) => r),
    });

  return (
    <ConfigFormShell
      title="CORS"
      description={`Cross-origin resource sharing rules for ${domain}.`}
      isLoading={isLoading}
      isConfigured={isConfigured}
      dirty={dirty}
      busy={busy}
      saving={update.isPending}
      statusLabel={enabled ? 'Enabled' : undefined}
      onSave={save}
      onRemove={() => remove.mutate()}
      drift={<CdnAspectDrift domainId={domainId} path="cors" />}
      onCancel={() => {
        if (snap) {
          const s = JSON.parse(snap);
          setEnabled(s.enabled);
          setRules(s.rules);
        }
      }}
    >
      <Card>
        <CardHeader
          actions={
            <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={addRule}>
              Add rule
            </Button>
          }
        >
          <CardTitle>Rules</CardTitle>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <SettingRow title="Enabled" control={<Switch checked={enabled} onCheckedChange={setEnabled} />} />
          {rules.length === 0 ? (
            <EmptyState size="sm" icon={<Globe size={20} />} title="No CORS rules yet" description="Add a rule to allow cross-origin requests." />
          ) : (
            rules.map((r) => {
              const open = expanded.has(r._id);
              return (
                <div key={r._id} style={{ border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-md)', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', padding: 'var(--vn-space-2) var(--vn-space-3)' }}>
                    <Badge tone="neutral" appearance="subtle" size="sm">{r.priority}</Badge>
                    <span style={{ flex: 1, fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)', fontSize: 'var(--vn-font-size-md)' }}>{r.path_pattern}</span>
                    <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{r.allow_origins.join(', ') || '—'}</span>
                    <IconButton variant="ghost" size="sm" aria-label="Remove" onClick={() => setRules((p) => p.filter((x) => x._id !== r._id))}>
                      <Trash2 size={15} />
                    </IconButton>
                    <IconButton variant="ghost" size="sm" aria-label="Toggle" onClick={() => setExpanded((s) => { const n = new Set(s); n.has(r._id) ? n.delete(r._id) : n.add(r._id); return n; })}>
                      {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </IconButton>
                  </div>
                  {open && (
                    <div style={{ borderTop: '1px solid var(--vn-border-default)', padding: 'var(--vn-space-4)', display: 'grid', gap: 'var(--vn-space-4)' }}>
                      <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                        <Field label="Path pattern">{(p) => <Input {...p} value={r.path_pattern} onChange={(e) => patch(r._id, { path_pattern: e.target.value })} />}</Field>
                        <Field label="Match type">
                          {(p) => (
                            <Select value={r.match_type} onValueChange={(v) => patch(r._id, { match_type: v })}>
                              <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                              <SelectContent>{MATCH_TYPES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                            </Select>
                          )}
                        </Field>
                        <Field label="Priority">{(p) => <Input {...p} type="number" value={String(r.priority)} onChange={(e) => patch(r._id, { priority: Number(e.target.value) || 0 })} />}</Field>
                        <Field label="Max age (s)">{(p) => <Input {...p} type="number" value={String(r.max_age_secs)} onChange={(e) => patch(r._id, { max_age_secs: Number(e.target.value) || 0 })} />}</Field>
                      </div>
                      <Field label="Allow origins" description="Comma-separated. Use * for any.">{(p) => <Input {...p} value={csv(r.allow_origins)} onChange={(e) => patch(r._id, { allow_origins: split(e.target.value) })} />}</Field>
                      <Field label="Allow methods" description="Comma-separated.">{(p) => <Input {...p} value={csv(r.allow_methods)} onChange={(e) => patch(r._id, { allow_methods: split(e.target.value) })} />}</Field>
                      <Field label="Allow headers" description="Comma-separated.">{(p) => <Input {...p} value={csv(r.allow_headers)} onChange={(e) => patch(r._id, { allow_headers: split(e.target.value) })} />}</Field>
                      <Field label="Expose headers" description="Comma-separated.">{(p) => <Input {...p} value={csv(r.expose_headers)} onChange={(e) => patch(r._id, { expose_headers: split(e.target.value) })} />}</Field>
                      <SettingRow title="Allow credentials" control={<Switch checked={r.allow_credentials} onCheckedChange={(v) => patch(r._id, { allow_credentials: v })} />} />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </CardBody>
      </Card>
    </ConfigFormShell>
  );
}
